export enum AsyncProcessStatus {
  QUEUED = "queued",
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
}

export type CollectorAccountExportStatus = "queued" | "processing" | "completed" | "failed";

export interface CollectorAccountExport {
  readonly id: number;
  readonly status: CollectorAccountExportStatus | string;
  readonly progress: number;
  readonly error_message?: string | null;
}

export interface CollectorAccountExportCreateResponse {
  readonly id: number;
}

/** Pending move scheduled for the next period */
export interface PendingMove {
  readonly bucket_id: number;
  readonly bucket_name: string;
  readonly phase_id: number;
  readonly phase_name: string | null;
}

export type PhaseAssignmentStatus = "available_soon" | "ready_to_assign" | "assigned";

/** Whether an action ran immediately or was deferred to the next period. */
export type CollectionActionMode = "immediate" | "deferred";

/** Collector inside `BucketPhase.collectors[]`; phase comes from the parent. */
export interface BucketCollector {
  readonly id: number;
  readonly assignment_id: number;
  readonly initials: string;
  readonly name: string;
  readonly assigned_clients_count: number;
  readonly assigned_debt: number;
  readonly pending_move: PendingMove | null;
  /** ISO date when the scheduled removal will apply. */
  readonly pending_removal_at: string | null;
}

/** Phase within a bucket (collectors are nested here) */
export interface BucketPhase {
  readonly id: number;
  readonly name: string;
  readonly sequence: number;
  readonly total_collectors: number;
  readonly total_clients: number;
  readonly total_debt: number;
  readonly collectors: BucketCollector[];
  readonly assignment_status: PhaseAssignmentStatus;
  /** Pre-formatted label for the status (e.g. "Listo para asignar"). */
  readonly assignment_status_message: string;
}

/** Single bucket configuration entry */
export interface BucketConfig {
  readonly id: number;
  readonly name: string;
  readonly label: string;
  readonly phases_label: string;
  readonly phases: BucketPhase[];
  readonly total_collectors: number;
  readonly total_clients: number;
  readonly total_debt: number;
}

/** Payload for PUT /api/preventive_collection/admin/move_collector */
export interface MoveCollectorPayload {
  readonly assignment_id: number;
  readonly target_bucket_id: number;
  readonly target_phase_id: number | null;
}

/** Response from PUT /api/preventive_collection/admin/move_collector */
export interface MoveCollectorResponse {
  readonly message: string;
  readonly mode: CollectionActionMode;
  readonly collector: {
    readonly id: number;
    readonly assignment_id: number;
    readonly initials: string;
    readonly name: string;
    readonly current_bucket: string;
    readonly current_phase: string | null;
    readonly pending_move: PendingMove | null;
  };
}

/** Response from PUT /api/preventive_collection/admin/cancel_move */
export interface CancelMoveResponse {
  readonly message: string;
  readonly assignment_id: number;
  readonly pending_move: PendingMove | null;
}

/** Response from DELETE /api/preventive_collection/admin/assignments/:id */
export interface DeleteAssignmentResponse {
  readonly message: string;
  readonly mode: CollectionActionMode;
  readonly assignment_id: number;
  readonly pending_removal_at: string | null;
}

/** Response from PUT /api/preventive_collection/admin/cancel_removal */
export interface CancelRemovalResponse {
  readonly message: string;
  readonly assignment_id: number;
  readonly pending_removal_at: string | null;
}

/** Single portfolio entry within the assignment summary */
export interface AssignmentPortfolio {
  readonly id: number;
  readonly name: string;
  readonly cutoff_day: number;
  readonly start_date: string;
}

/** Summary of assignment status from bucket_configuration */
export interface AssignmentSummary {
  readonly portfolios: AssignmentPortfolio[];
  readonly total_collectors: number;
  readonly already_assigned: boolean;
}

/** Collector available for assignment (not yet in a bucket) */
export interface AvailableCollector {
  readonly id: number;
  readonly name: string;
  readonly initials: string;
}

/** Response from GET /api/preventive_collection/admin/bucket_configuration */
export interface BucketConfigurationResponse {
  readonly next_period_end_date: string;
  readonly buckets: BucketConfig[];
  readonly assignment_summary?: AssignmentSummary;
  readonly available_collectors?: AvailableCollector[];
}

/** Payload for POST /api/preventive_collection/admin/assignments */
export interface CreateAssignmentPayload {
  readonly collector_id: number;
  readonly bucket_id: number;
  readonly phase_id: number | null;
}

/** @deprecated The synchronous assignment endpoint is being retired. */
export interface AssignPortfolioResponse {
  readonly message: string;
}

export type PortfolioAssignmentRunStatus = AsyncProcessStatus;

export interface PortfolioAssignmentRunSummary {
  readonly eligible_accounts: number;
  readonly assigned_accounts: number;
  readonly unassigned_accounts: number;
}

export interface PortfolioAssignmentRunGap {
  readonly bucket_id: number;
  readonly bucket_name: string;
  readonly phase_id: number;
  readonly phase_sequence: number;
}

export interface PortfolioAssignmentRunError {
  readonly code: string;
  readonly message: string;
  readonly gaps?: readonly PortfolioAssignmentRunGap[];
}

/** Resource returned by the portfolio assignment runs API. */
export interface PortfolioAssignmentRun {
  readonly id: number;
  readonly status: PortfolioAssignmentRunStatus;
  readonly portfolio_cycle_half_id?: number;
  readonly bucket_phase_ids?: readonly number[];
  readonly executor_id?: number;
  readonly created_at?: string;
  readonly started_at?: string | null;
  readonly completed_at?: string | null;
  readonly failed_at?: string | null;
  readonly summary: PortfolioAssignmentRunSummary | null;
  readonly error: PortfolioAssignmentRunError | null;
}

/** Gestor item from GET /admin/collectors?compact=true */
export interface CollectorOption {
  readonly id: number;
  readonly name: string;
  readonly bucket: string | null;
  readonly phase: string | null;
}

/** Period block from GET /api/preventive_collection/collector_accounts/performance */
export interface CollectorPerformancePeriod {
  readonly label: string;
  readonly start_date: string;
  readonly end_date: string;
  readonly cutoff_day: number;
}

/** Daily point from GET .../performance `daily_recovery` */
export interface CollectorPerformanceDailyRecovery {
  readonly date: string;
  readonly recovered_amount: number;
  /** Running sum through this day (MXN). */
  readonly cumulative_recovered_amount?: number;
  /** Running % of assigned debt through this day (0–100, integer). */
  readonly cumulative_progress_percent?: number;
}

/** Period history row from GET .../performance `period_history` */
export interface CollectorPerformancePeriodHistory {
  readonly period_label: string;
  readonly assigned_accounts_count: number;
  readonly bucket_name: string;
  readonly phase_name: string | null;
  readonly assigned_amount: number;
  readonly recovered_amount: number;
  readonly progress_percent: number;
}

/** Response from GET /api/preventive_collection/collector_accounts/performance */
export interface CollectorPerformanceSummary {
  readonly period: CollectorPerformancePeriod;
  readonly assigned_accounts_count: number;
  readonly assigned_amount: number;
  readonly recovered_amount: number;
  readonly progress_percent: number;
  readonly daily_recovery?: ReadonlyArray<CollectorPerformanceDailyRecovery>;
  readonly period_history?: ReadonlyArray<CollectorPerformancePeriodHistory>;
}
